import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import useIsInFactoryUpgradesQualiExperiment, {
  QUALI_FACTORY_UPGRADES_NAME
} from '~/hooks/common/experiments/useIsInFactoryUpgradesQualiExperiment';
import BrowserCookies from '~/utilities/BrowserCookies';
import {
  loadItemFromLocalStorage,
  saveItemToLocalStorage
} from '~/utilities/localStorage';

const STORAGE_KEY = 'common/factoryUpgradesQualiVehicleIds';
const REQUIRED_ENGAGEMENTS = 3;

function loadStoredIds(): string[] {
  try {
    return loadItemFromLocalStorage<string[]>(STORAGE_KEY) ?? [];
  } catch {
    return [];
  }
}

type ContextType = {
  captureEngagement: (vehicleId: number) => void;
  hasMetCriteria: boolean;
};

const defaultContext: ContextType = {
  captureEngagement() {},
  hasMetCriteria: false
};

const FactoryUpgradesQualiContext = createContext<ContextType>(defaultContext);

export function FactoryUpgradesQualiProvider({
  children
}: React.PropsWithChildren<{}>) {
  const isInExperiment = useIsInFactoryUpgradesQualiExperiment();
  const initialIds = loadStoredIds();
  const vehicleIdsRef = useRef<string[]>(initialIds);
  const [hasMetCriteria, setHasMetCriteria] = useState(
    initialIds.length >= REQUIRED_ENGAGEMENTS
  );

  const hasBeenAsked = useMemo(
    () =>
      BrowserCookies.get(`${QUALI_FACTORY_UPGRADES_NAME}-asked`) === 'asked',
    []
  );

  const captureEngagement = useCallback(
    (vehicleId: number) => {
      if (!isInExperiment || hasBeenAsked) return;

      const id = vehicleId.toString();
      const current = vehicleIdsRef.current;

      if (current.includes(id)) return;

      const updated = [...current, id];
      vehicleIdsRef.current = updated;
      saveItemToLocalStorage(STORAGE_KEY, updated);

      if (updated.length >= REQUIRED_ENGAGEMENTS) {
        setHasMetCriteria(true);
      }
    },
    [hasBeenAsked, isInExperiment]
  );

  useEffect(() => {
    if (!hasBeenAsked) return;

    vehicleIdsRef.current = [];
    saveItemToLocalStorage(STORAGE_KEY, []);
    setHasMetCriteria(false);
  }, [hasBeenAsked]);

  const value = useMemo<ContextType>(
    () => ({ captureEngagement, hasMetCriteria }),
    [captureEngagement, hasMetCriteria]
  );

  return (
    <FactoryUpgradesQualiContext.Provider value={value}>
      {children}
    </FactoryUpgradesQualiContext.Provider>
  );
}

FactoryUpgradesQualiProvider.displayName = 'FactoryUpgradesQualiProvider';

export function useFactoryUpgradesQuali() {
  return useContext(FactoryUpgradesQualiContext);
}

export function useQualiFactoryUpgradesCriteria() {
  return useFactoryUpgradesQuali().hasMetCriteria;
}

export default FactoryUpgradesQualiContext;
